import * as Sentry from '@sentry/react';
import React, { useMemo } from 'react';

import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { useShowToast } from 'src/components/toast/Toast';
import { useCloudRoutingConfigurationPageQuery } from 'src/hooks/useCloudRoutingConfigurationPageQuery';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { getRouterIsInitiating } from 'src/hooks/useIsCloudEndpointInitiating';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { useUpsertRouterConfig } from 'src/hooks/useUpsertRouterConfig';

import { graphRefToString, useGraphRef } from '../hooks/useGraphRef';

import { RouterConfigEditor } from './components/routerConfigEditor/RouterConfigEditor';
import { SecretsList } from './components/secretsList/SecretsList';

/**
 * Component to display cloud routing configuration. It provides the interface for
 * editing router configuration and displaying related secrets.
 *
 * @function CloudRoutingConfigurationPage
 * @returns {React.ReactNode}
 */
export const CloudRoutingConfigurationPage = () => {
  const [orgId] = useCurrentAccountId();
  const graphRef = useGraphRef();
  const graphRefString = graphRefToString(graphRef);
  const trackCustomEvent = useTrackCustomEvent();
  const { showToasts } = useShowToast();
  const { upsertRouterConfig, upsertRouterError, upsertResponseData } =
    useUpsertRouterConfig();

  /**
   * Cloud Routing Configuration query. It will fetch related data for the
   * cloud routing configuration page.
   */
  const { data, loading } = useCloudRoutingConfigurationPageQuery({
    serviceId: graphRef?.graphId ?? '',
    graphVariant: graphRef?.graphVariant ?? '',
    accountId: orgId ?? '',
    graphRef: graphRefString ?? '',
    skip: !graphRef || !orgId,
  });

  /**
   * Handler to save the YAML router configuration that can be edited in Markdown.
   *
   * @function handleSaveRouterConfig
   * @param {string} newRouterConfig - The new router configuration to save.
   * @returns {Promise<void>}
   */
  const handleSaveRouterConfig = React.useCallback(
    async (newRouterConfig: string) => {
      if (!graphRef) return;
      try {
        const upsertRouterConfigResult = await upsertRouterConfig({
          graphId: graphRef.graphId,
          variant: graphRef.graphVariant,
          newRouterConfig,
        });
        if (
          upsertRouterConfigResult.data?.service?.variant?.upsertRouterConfig
            ?.__typename !== 'RouterUpsertFailure' &&
          !upsertRouterConfigResult.errors
        ) {
          // successfully updated
          trackCustomEvent({
            action: 'saved_router_config',
            category: 'Cloud Router Settings',
            label: `graphRef:${graphRefToString(graphRef)}`,
          });
          showToasts({
            heading: (
              <span className="text-base font-normal text-black">
                Your router configuration changes have been saved.
              </span>
            ),
            level: 'success',
            stableId: 'save-router-config-success',
            message: false,
            dismissable: true,
          });
        }
      } catch (err) {
        Sentry.captureException(err);
      }
    },
    [graphRef, showToasts, trackCustomEvent, upsertRouterConfig],
  );

  /**
   * Determine if the router is currently being created.
   * By default, it is set to true until data is returned.
   *
   * @const routerIsInitiating
   * @type {boolean}
   */
  const routerIsInitiating = useMemo(
    () =>
      !data?.service?.variant ||
      getRouterIsInitiating({
        graphType: data.service.graphType,
        latestLaunch: data.service.variant?.latestLaunch,
        routerStatus: data.service.variant?.router?.status,
      }),
    [data?.service?.graphType, data?.service?.variant],
  );

  /**
   * Retrieve the list of all subgraphs for this router.
   *
   * @const allSubgraphs
   * @type {string[]}
   */
  const allSubgraphs = useMemo(
    () => data?.service?.variant?.subgraphs?.map(({ name }) => name) ?? [],
    [data?.service?.variant?.subgraphs],
  );

  if (loading) {
    return (
      <div className="grid h-full place-items-center">
        <Loading size="lg" />
      </div>
    );
  }

  if (!data?.service) {
    return <NotFound />;
  }

  const routerStatus = data.service.variant?.router?.status;
  const routerConfigSchema =
    data.service.variant?.router?.routerVersion?.configSchema;

  return (
    <div className="mx-8 mb-12 mt-4 h-full">
      <h1 className="mb-4 text-2xl font-semibold">Configuration</h1>
      {/* Secrets Card */}
      <SecretsList
        className="my-8"
        status={routerStatus}
        isInitiating={routerIsInitiating}
      />
      {/* Router Configuration Markdown Editor */}
      <RouterConfigEditor
        canEditBuildConfig={
          data?.service?.variant?.permissions?.canManageBuildConfig
        }
        canQueryBuildConfig={
          data?.service?.variant?.permissions?.canQueryBuildConfig
        }
        defaultValue={data?.service?.variant?.routerConfig ?? ''}
        handleSaveRouterConfig={handleSaveRouterConfig}
        upsertRouterError={upsertRouterError}
        upsertResponseData={upsertResponseData}
        configSchema={routerConfigSchema}
        allSubgraphs={allSubgraphs}
      />
    </div>
  );
};
